import { useMemo } from 'react';
import {
  Frame,
  Grid,
  SageClassnames,
  useSageBreakpoint,
  Card,
  SageTokens,
  Icon,
} from '@kajabi/sage-react';

interface TransformedUnsplashObject {
  image_url: string;
  alt_description?: string;
}

interface PropsType {
  userData: any;
  previewImageUrls: TransformedUnsplashObject[];
  heroImageUrl: string;
}

const CheckoutPreview = ({
  userData,
  previewImageUrls,
  heroImageUrl,
}: PropsType) => {
  const breakpoints = useSageBreakpoint();

  const checkoutData = useMemo(() => {
    return userData.ai_data.checkouts;
  }, [userData]);

  const checkoutPreviewLeftSide = () => (
    <Grid.Col
      className={SageClassnames.SPACERS[breakpoints.sm ? 'NONE' : 'SM_RIGHT']}
    >
      <Frame
        style={{
          maxWidth: breakpoints.sm ? '100%' : '276px',
          paddingTop: breakpoints.sm && '16px',
        }}
        gap={Frame.GAPS.SM}
        className={SageClassnames.SPACERS.XS_BOTTOM}
        padding={Frame.PADDINGS.NONE}
      >
        <Frame
          style={{
            minHeight: '172px',
            backgroundImage: `
                      url(${previewImageUrls.slice(-2)[0]?.image_url})
                    `,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            borderRadius: '8px',
          }}
          padding={Frame.PADDINGS.LG}
          gap={Frame.GAPS.XS}
          align={Frame.ALIGNMENTS.CENTER_CENTER}
        />
        <h4 className={SageClassnames.TYPE.HEADING_4}>{checkoutData.title}</h4>
        <div>
          <h6 className={SageClassnames.TYPE.HEADING_6}>
            {checkoutData.subtitle}
          </h6>
          <p className={SageClassnames.TYPE.BODY_SMALL}>
            {checkoutData.description}
          </p>
        </div>
      </Frame>
    </Grid.Col>
  );

  const checkoutFormsArray = [
    <div style={{ display: 'inline-flex' }}>
      <Icon style={{ marginRight: '24px' }} icon={Icon.ICONS.CARD_GENERIC} />
      <p
        className={`${SageClassnames.TYPE_COLORS.CHARCOAL_200} ${SageClassnames.TYPE.BODY_SMALL_MED}`}
      >
        Card
      </p>
    </div>,
    <div style={{ display: 'inline-flex' }}>
      <img
        src="https://kajabi-app-assets.kajabi-cdn.com/ai-lead-gen/AppleIcon.png"
        loading="lazy"
        alt=""
        className="popup_social-icon"
        style={{
          height: '16px',
          width: '16px',
          marginRight: '24px',
        }}
      />
      <p
        className={`${SageClassnames.TYPE_COLORS.CHARCOAL_200} ${SageClassnames.TYPE.BODY_SMALL_MED}`}
      >
        Apple Pay
      </p>
    </div>,
    <div style={{ display: 'inline-flex' }}>
      <img
        src="https://kajabi-app-assets.kajabi-cdn.com/ai-lead-gen/GoogleIcon.png"
        loading="lazy"
        alt=""
        className="popup_social-icon"
        style={{
          height: '16px',
          width: '16px',
          marginRight: '24px',
        }}
      />
      <p
        className={`${SageClassnames.TYPE_COLORS.CHARCOAL_200} ${SageClassnames.TYPE.BODY_SMALL_MED}`}
      >
        Google Pay
      </p>
    </div>,
  ];

  return (
    <Frame
      style={{
        width: breakpoints.lg ? '100%' : '70%',
        border: breakpoints.sm ? '1rem solid #eceeef' : null,
        backgroundColor: breakpoints.sm ? '#eceeef' : null,
        borderRadius: breakpoints.sm ? '32px' : '15px',
        boxShadow: breakpoints.sm ? 'rgba(0, 0, 0, 0.1) 0px 20px 40px' : null,
      }}
      align={Frame.ALIGNMENTS.STRETCH_CENTER}
    >
      <Frame
        gap={Frame.GAPS.NONE}
        className={`container ${
          SageClassnames.SPACERS[breakpoints.md ? 'NONE' : 'LG_TOP']
        }`}
        style={{
          width: [breakpoints.md ? '100%' : '732px'],
          boxShadow: breakpoints.sm ? null : 'rgba(0, 0, 0, 0.1) 0px 20px 40px',
          borderRadius: breakpoints.sm ? '24px' : '15px',
          height: breakpoints.md ? '576px' : '720px',
          overflowY: 'scroll',
        }}
      >
        {/* Mock window component with 3 buttons */}
        {breakpoints.sm || (
          <Frame
            background="#ECEEEF"
            style={{
              height: '42px',
              borderTopLeftRadius: '15px',
              borderTopRightRadius: '15px',
            }}
          >
            <Frame
              direction={Frame.DIRECTIONS.HORIZONTAL}
              padding={Frame.PADDINGS.SM}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                }}
              ></div>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                  marginLeft: '-15px',
                }}
              ></div>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                  marginLeft: '-15px',
                }}
              ></div>
            </Frame>
          </Frame>
        )}

        {/* Checkout mobile navigation component */}
        {breakpoints.sm && (
          <Frame
            background="#FFFFFF"
            style={{ height: '56px' }}
            direction={Frame.DIRECTIONS.HORIZONTAL}
            padding={Frame.PADDINGS.SM}
          >
            <Frame
              padding={Frame.PADDINGS.XS}
              direction={Frame.DIRECTIONS.HORIZONTAL}
            >
              <div
                style={{
                  width: 87,
                  height: breakpoints.sm ? 8 : 12,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              ></div>
            </Frame>
            <Frame
              padding={Frame.PADDINGS.NONE}
              align={Frame.ALIGNMENTS.TOP_RIGHT}
              gap={Frame.GAPS.XXS}
            >
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
            </Frame>
          </Frame>
        )}

        {/* Checkout page hero image component */}
        <Frame
          style={{
            minHeight: breakpoints.sm ? '78px' : '124px',
            backgroundImage: `
                        url(${heroImageUrl})
                    `,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
          padding={Frame.PADDINGS.LG}
          gap={Frame.GAPS.XS}
          align={Frame.ALIGNMENTS.CENTER_CENTER}
        ></Frame>

        {/* Checkout preview body components */}
        <Frame
          background="#FFFFFF"
          gap={Frame.GAPS.LG}
          padding={Frame.PADDINGS[breakpoints.sm ? 'MD' : 'XL']}
          direction={
            Frame.DIRECTIONS[breakpoints.sm ? 'VERTICAL' : 'HORIZONTAL']
          }
          style={{ width: '100%', paddingTop: breakpoints.sm && '16px' }}
        >
          {/* Left side with image and text */}
          <Grid.Row>
            {breakpoints.sm || checkoutPreviewLeftSide()}
            {/* Right side with mock checkout preview */}
            <Grid.Col
              className={
                SageClassnames.SPACERS[breakpoints.sm ? 'NONE' : 'SM_LEFT']
              }
            >
              <Frame
                style={{
                  backgroundColor: 'white',
                  width: breakpoints.sm ? '100%' : '312px',
                  borderRadius: '4px',
                }}
                gap={Frame.GAPS.SM}
              >
                <Card
                  style={{
                    height: '389px',
                    boxShadow: 'rgba(0, 0, 0, 0.15) 0px 8px 36px 0px',
                    width: 'inherit',
                    padding: '16px',
                  }}
                >
                  <Card
                    style={{
                      maxHeight: '40px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '1rem',
                      borderRadius: '8px',
                    }}
                  >
                    <p
                      className={`${SageClassnames.TYPE_COLORS.CHARCOAL_100} ${SageClassnames.TYPE.BODY_SMALL}`}
                    >
                      Email address
                    </p>
                  </Card>
                  <Card
                    style={{ gap: 'initial' }}
                    clearPaddingTop
                    clearPaddingBottom
                  >
                    <Card.List
                      items={checkoutFormsArray}
                      itemGridTemplate={SageTokens.GRID_TEMPLATES.ET}
                      blockSpacing="sm"
                    />
                    <Card.Row
                      gridTemplate={SageTokens.GRID_TEMPLATES.ET}
                      style={{ flexFlow: 'nowrap', justifyContent: 'left' }}
                    >
                      <img
                        src="https://kajabi-app-assets.kajabi-cdn.com/ai-lead-gen/AfterpayIcon.png"
                        loading="lazy"
                        alt=""
                        className="popup_social-icon"
                        style={{
                          height: '16px',
                          width: '16px',
                        }}
                      />
                      <div
                        style={{
                          paddingTop: '0.4rem',
                          paddingBottom: '0.4rem',
                        }}
                        className={`${SageClassnames.TYPE_COLORS.CHARCOAL_200} ${SageClassnames.TYPE.BODY_SMALL_MED}`}
                      >
                        Afterpay
                        <p
                          className={SageClassnames.TYPE_COLORS.CHARCOAL_100}
                          style={{
                            fontSize: '10px',
                          }}
                        >
                          Buy now pay later
                        </p>
                      </div>
                    </Card.Row>
                  </Card>
                  <Card.Footer style={{ alignItems: 'center' }}>
                    <div
                      style={{
                        width: 280,
                        height: 40,
                        backgroundColor: SageTokens.COLOR_PALETTE.PRIMARY_300,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <p
                        style={{
                          fontSize: '16px',
                          fontWeight: '700',
                          color: 'white',
                          margin: 'auto',
                        }}
                      >
                        Pay now
                      </p>
                    </div>
                  </Card.Footer>
                </Card>
              </Frame>
            </Grid.Col>
            {breakpoints.sm && checkoutPreviewLeftSide()}
          </Grid.Row>
        </Frame>
      </Frame>
    </Frame>
  );
};

export default CheckoutPreview;
